import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import './AanvragenDashboard.css';

const STATUS = {
  TE_VERWERKEN: 'te_verwerken',
  AFGEVINKT: 'afgevinkt',
  VERWERKT: 'verwerkt'
};

function getStatus(req) {
  if (req.used_at) return STATUS.VERWERKT;
  if (req.dismissed_at) return STATUS.AFGEVINKT;
  return STATUS.TE_VERWERKEN;
}

const AanvragenDashboard = () => {
  const navigate = useNavigate();
  const { user, selectedSubscriberId } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(STATUS.TE_VERWERKEN);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [addingExample, setAddingExample] = useState(false);

  const load = async () => {
    if (!selectedSubscriberId && user?.role === 'admin') {
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/aanvragen', {
        headers: selectedSubscriberId ? { 'X-Subscriber-Id': selectedSubscriberId } : {}
      });
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Aanvragen laden mislukt.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [selectedSubscriberId]);

  const filtered = requests.filter((r) => getStatus(r) === filter);
  const counts = {
    [STATUS.TE_VERWERKEN]: requests.filter((r) => getStatus(r) === STATUS.TE_VERWERKEN).length,
    [STATUS.AFGEVINKT]: requests.filter((r) => getStatus(r) === STATUS.AFGEVINKT).length,
    [STATUS.VERWERKT]: requests.filter((r) => getStatus(r) === STATUS.VERWERKT).length
  };

  const handleVerwerken = (req) => {
    setActioningId(req.id);
    navigate('/', { state: { applyOfferRequestId: req.id } });
    setActioningId(null);
  };

  const handleAfvinken = async (req) => {
    setActioningId(req.id);
    try {
      await api.post(`/api/aanvragen/${req.id}/dismiss`);
      await load();
    } catch (err) {
      setError('Afvinken mislukt.');
    } finally {
      setActioningId(null);
    }
  };

  const handleAddExample = async () => {
    setAddingExample(true);
    setError(null);
    try {
      await api.post('/api/aanvragen/voorbeeld', {}, {
        headers: selectedSubscriberId ? { 'X-Subscriber-Id': selectedSubscriberId } : {}
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Voorbeeldaanvraag toevoegen mislukt.');
    } finally {
      setAddingExample(false);
    }
  };

  const statusLabel = {
    [STATUS.TE_VERWERKEN]: 'Te verwerken',
    [STATUS.AFGEVINKT]: 'Afgevinkt',
    [STATUS.VERWERKT]: 'Verwerkt'
  };

  return (
    <div className="aanvragen-dashboard">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>Aanvragendashboard</h1>
            <p className="dashboard-subtitle">
              Inkomende offerte-aanvragen via de API. Verwerken = offerte starten. Afvinken = afgehandeld zonder offerte.
            </p>
          </div>
          {selectedSubscriberId && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={addingExample}
              onClick={handleAddExample}
            >
              {addingExample ? 'Toevoegen…' : '+ Voorbeeldaanvraag'}
            </button>
          )}
        </div>

        {(!selectedSubscriberId && user?.role === 'admin') && (
          <div className="card card-warning">
            Selecteer een bedrijf in de header om aanvragen te zien.
          </div>
        )}

        {error && <div className="error">{error}</div>}

        <div className="filter-tabs">
          {[STATUS.TE_VERWERKEN, STATUS.AFGEVINKT, STATUS.VERWERKT].map((key) => (
            <button
              key={key}
              type="button"
              className={`tab ${filter === key ? 'active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {statusLabel[key]}
              <span className="badge">{counts[key]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading">Aanvragen laden...</div>
        ) : (
          <div className="request-list">
            {filtered.length === 0 ? (
              <div className="empty-state">
                {filter === STATUS.TE_VERWERKEN && 'Geen aanvragen te verwerken.'}
                {filter === STATUS.AFGEVINKT && 'Geen afgevinkte aanvragen.'}
                {filter === STATUS.VERWERKT && 'Geen verwerkte aanvragen.'}
              </div>
            ) : (
              filtered.map((req) => {
                const status = getStatus(req);
                const isActioning = actioningId === req.id;
                return (
                  <div key={req.id} className="request-card">
                    <div className="request-main">
                      <div className="request-meta">
                        <strong className="request-aanvrager">{req.aanvrager}</strong>
                        <span className="request-detail">
                          {(req.products || []).length} product(en)
                          {req.customer?.name && ` · ${req.customer.name}`}
                        </span>
                        <div className="request-date">
                          {req.created_at ? new Date(req.created_at).toLocaleString('nl-NL') : ''}
                          {status === STATUS.VERWERKT && req.used_at && (
                            <span> · Verwerkt {new Date(req.used_at).toLocaleDateString('nl-NL')}</span>
                          )}
                          {status === STATUS.AFGEVINKT && req.dismissed_at && (
                            <span> · Afgevinkt {new Date(req.dismissed_at).toLocaleDateString('nl-NL')}</span>
                          )}
                        </div>
                      </div>
                      <div className="request-actions">
                        {status === STATUS.TE_VERWERKEN && (
                          <>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={isActioning}
                              onClick={() => handleVerwerken(req)}
                            >
                              Verwerken
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={isActioning}
                              onClick={() => handleAfvinken(req)}
                            >
                              Afvinken
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {(req.products?.length > 0 || req.customer || req.opmerkingen) && (
                      <details className="request-details">
                        <summary>Details</summary>
                        {req.opmerkingen && (
                          <div className="detail-block">
                            <strong>Opmerkingen:</strong>
                            <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{req.opmerkingen}</p>
                          </div>
                        )}
                        {req.products?.length > 0 && (
                          <div className="detail-block">
                            <strong>Producten:</strong>
                            <ul>
                              {(req.products || []).map((p, i) => (
                                <li key={i}>
                                  {p.product_id ? `Product-id: ${p.product_id}` : `${p.name || '—'} · € ${(p.price || 0).toFixed(2)}`}
                                  {p.quantity > 1 && ` · ${p.quantity}x`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {req.customer && (
                          <div className="detail-block">
                            <strong>Klant:</strong>
                            <pre>{JSON.stringify(req.customer, null, 2)}</pre>
                          </div>
                        )}
                      </details>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        <details className="api-voorbeeld-card">
          <summary>API-voorbeeld: zo ziet een aangeleverde aanvraag eruit</summary>
          <p className="api-voorbeeld-uitleg">
            Externe systemen sturen <code>POST /api/offer-requests/{'{subscriberId}'}</code> met header <code>X-Api-Key</code>. 
            Klantgegevens en optioneel contactpersoon worden in stap 3 vooringevuld.
          </p>
          <pre className="api-voorbeeld-json">{`{
  "aanvrager": "Website formulier",
  "opmerkingen": "Klant wil een moderne website. Graag binnen 6 weken opleveren.",
  "products": [
    { "product_id": "uuid-bestaand-product", "quantity": 2 },
    { "name": "Extra werk", "price": 500, "quantity": 1 }
  ],
  "customer": {
    "name": "Voorbeeld B.V.",
    "email": "info@voorbeeld.nl",
    "phone": "020 1234567",
    "street": "Keizersgracht 100",
    "city": "Amsterdam",
    "zip": "1015 AA",
    "vat": "NL123456789B01",
    "contact_name": "Jan de Vries",
    "contact_email": "jan@voorbeeld.nl",
    "contact_phone": "06 12345678"
  }
}`}</pre>
        </details>

        <div className="dashboard-footer">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
            ← Terug naar offertes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AanvragenDashboard;
